import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Star, Flame, Leaf, Battery, Heart, Zap, FlaskConical, Activity, Shield, Droplets } from "lucide-react";
import useSEO from "@/hooks/useSEO";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_wellness-shots/artifacts/dibvmg73_kens%20gold.png";

const gingerBenefits = [
  "Powerful anti-inflammatory properties",
  "Relieves nausea and digestive discomfort",
  "Boosts metabolism and thermogenesis",
  "Supports immune system function",
  "Natural pain and muscle relief",
];

const honeyBenefits = [
  "Natural antibacterial & antifungal properties",
  "Rich in antioxidants & phytonutrients",
  "Soothes sore throats and coughs",
  "Provides clean, sustained energy",
];

const lemonBenefits = [
  "High in Vitamin C for immune defense",
  "Alkalizes the body's pH balance",
  "Supports healthy digestion",
  "Brightens skin and fights free radicals",
];

const acvBenefits = [
  "Balances blood sugar levels naturally",
  "Supports healthy gut microbiome",
  "Aids natural detoxification pathways",
  "Helps manage a healthy weight",
];

const greenTeaBenefits = [
  "Rich in catechins & EGCG antioxidants",
  "Enhances brain function and focus",
  "Supports natural fat burning",
  "Calming energy \u2014 no coffee crash",
];

const whatYoullFeel = [
  { id: "ginger-kick", icon: <Flame size={18} />, text: "A warm ginger kick within 10\u201320 seconds" },
  { id: "breathing", icon: <Leaf size={18} />, text: "Clearer breathing and open airways" },
  { id: "energy", icon: <Battery size={18} />, text: "Clean, focused energy without the crash" },
  { id: "digest", icon: <Heart size={18} />, text: "A light digestive reset" },
  { id: "wakefulness", icon: <Zap size={18} />, text: "A sense of wakefulness \u2014 calm but alert" },
];

const whyItWorksItems = [
  { id: "ginger", name: "Organic Ginger", action: "supports digestion & circulation" },
  { id: "lemon", name: "Organic Lemon", action: "delivers natural vitamin C" },
  { id: "honey", name: "Raw Honey", action: "antioxidants + smooths the burn" },
  { id: "acv", name: "Organic ACV", action: "supports gut balance" },
  { id: "greentea", name: "Organic Green Tea", action: "clean, focused energy" },
];

const bioactives = [
  { id: "ginger", ingredient: "Organic Ginger", compounds: "Gingerols, Shogaols", benefit: "Stimulates gastric emptying, reduces systemic inflammation, and alleviates nausea." },
  { id: "greentea", ingredient: "Organic Green Tea", compounds: "EGCG (Epigallocatechin gallate), L-Theanine", benefit: "High-potency antioxidant protection, metabolic activation, and clean cellular defense." },
  { id: "lemon", ingredient: "Organic Lemon", compounds: "Vitamin C (Ascorbic Acid), Citric Acid, Limonoids", benefit: "Enhances nutrient absorption, supports liver detox pathways, and aids kidney health." },
  { id: "honey", ingredient: "Raw Honey", compounds: "Diastase, Invertase, Hydrogen Peroxide compounds", benefit: "Antimicrobial, soothing prebiotic agent that acts as a natural delivery mechanism." },
  { id: "acv", ingredient: "Organic ACV", compounds: "Acetic Acid, Acetobacter (probiotics)", benefit: "Stabilizes blood sugar response, lowers stomach pH for better digestion, and supports the gut microbiome." },
];

const synergyEffects = [
  {
    id: "egcg-booster",
    icon: <FlaskConical size={20} />,
    title: "The Vitamin C + Catechin Booster",
    subtitle: "EGCG Bioavailability",
    text: "Green tea is packed with EGCG, one of the most powerful plant antioxidants known — but EGCG is naturally unstable in the neutral-to-alkaline environment of the human digestive tract, so your body typically absorbs less than 20% of it. The ascorbic acid (Vitamin C) and citric acid in fresh lemon juice create an acidic micro-environment in the gut. Research shows this acidity stabilizes catechins, increasing EGCG absorption by up to 5 times.",
  },
  {
    id: "blood-sugar",
    icon: <Activity size={20} />,
    title: "Dual-Action Blood Sugar & Insulin Control",
    subtitle: "Acetic Acid + Gingerols",
    text: "Both acetic acid (from ACV) and gingerols (from ginger) improve insulin sensitivity through different pathways. Acetic acid inhibits the enzymes that digest complex carbohydrates, slowing sugar absorption into the bloodstream. Simultaneously, ginger helps muscle cells take up glucose independently of high insulin spikes. Taking them together provides a dual layer of metabolic stabilization.",
  },
  {
    id: "gut-cascade",
    icon: <Droplets size={20} />,
    title: "Gut Microbiome & Digestive Cascade",
    subtitle: "Prebiotics + Probiotics",
    text: "Digestion relies on proper acid levels and peristalsis (gut motility). ACV provides acetic acid to lower stomach pH, signaling the stomach to release digestive enzymes. Ginger then accelerates gastric emptying so food moves smoothly into the small intestine. Finally, the prebiotics in raw honey and probiotics in raw ACV feed beneficial gut bacteria, supporting mucosal lining health.",
  },
  {
    id: "immune-shield",
    icon: <Shield size={20} />,
    title: "Anti-Inflammatory & Immune Shield",
    subtitle: "Multi-Angle Protection",
    text: "Raw honey provides natural antimicrobial action against pathogens, while gingerols inhibit pro-inflammatory pathways (COX-2 enzymes, similar to how mild NSAIDs work). Combined with the high Vitamin C from lemon and the cellular protection of green tea's EGCG, this blend targets inflammation from multiple biological angles at once.",
  },
];

/* ─── Shared sub-component ─── */

function BenefitList({ items }) {
  return (
    <ul className="space-y-2.5">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
          <CheckCircle size={16} className="text-gold mt-0.5 flex-shrink-0" />
          {item}
        </li>
      ))}
    </ul>
  );
}

/* ─── Section sub-components ─── */

function BenefitsHero() {
  return (
    <section className="benefits-hero text-center py-16 md:py-24 px-6" data-testid="benefits-hero">
      <img src={LOGO_URL} alt="Ken's Gold" className="w-48 h-48 md:w-56 md:h-56 mx-auto mb-8 object-contain" />
      <p className="text-xs font-bold tracking-widest text-gold uppercase mb-3">5 Power Ingredients</p>
      <h1 className="font-playfair text-4xl md:text-6xl font-bold text-brown-dark leading-tight mb-4" data-testid="benefits-title">
        The Science of <br /><span className="text-gold italic">Ken's Gold</span>
      </h1>
      <p className="text-gray-600 max-w-xl mx-auto text-base leading-relaxed">
        Each ingredient was chosen for its proven health benefits. Together, they form a synergistic blend that supports immunity, digestion, energy, and overall wellness.
      </p>
    </section>
  );
}

function FeelSection() {
  return (
    <section className="py-16 md:py-20 px-6 bg-cream" data-testid="benefits-feel-section">
      <div className="max-w-3xl mx-auto text-center">
        <p className="text-xs font-bold tracking-widest text-gold uppercase mb-3">After Your First Shot</p>
        <h2 className="font-playfair text-3xl md:text-4xl font-bold text-brown-dark mb-4">What You'll Feel</h2>
        <p className="text-gray-500 text-sm mb-10">This isn't a supplement you take and forget. You'll know it's working.</p>
        <div className="flex flex-wrap justify-center gap-3">
          {whatYoullFeel.map((item) => (
            <div key={item.id} className="flex items-center gap-3 bg-white border border-gold/10 rounded-full px-5 py-3 transition-all hover:border-gold/30 hover:shadow-sm" data-testid={`benefits-feel-${item.id}`}>
              <span className="text-gold">{item.icon}</span>
              <span className="text-brown-dark text-sm font-medium">{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function GingerAndHoneySection() {
  return (
    <section className="py-16 px-6" data-testid="ginger-section">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 ginger-bg rounded-2xl overflow-hidden flex flex-col md:flex-row">
          <div className="p-8 md:p-12 flex-1">
            <h2 className="font-playfair text-3xl md:text-4xl font-bold text-gold mb-2">Organic Ginger</h2>
            <p className="text-gold/60 text-xs font-semibold italic mb-4">The warm kick that fires up your system.</p>
            <p className="text-gray-600 text-sm leading-relaxed mb-6">
              For thousands of years, ginger has been revered in traditional medicine. Rich in gingerols and shogaols, this root is nature's most versatile healer — from fighting inflammation to calming nausea. You'll feel the heat spreading within seconds.
            </p>
            <BenefitList items={gingerBenefits} />
          </div>
          <div className="md:w-72 h-64 md:h-auto">
            <img src="https://images.unsplash.com/photo-1758055660285-d32b7b3b1e77?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" alt="Ginger root" className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="lg:col-span-2 honey-bg rounded-2xl overflow-hidden p-8" data-testid="honey-card">
          <div className="w-40 h-40 mx-auto rounded-xl overflow-hidden mb-6">
            <img src="https://images.pexels.com/photos/8851478/pexels-photo-8851478.jpeg?auto=compress&cs=tinysrgb&w=800" alt="Raw Honey" className="w-full h-full object-cover" />
          </div>
          <h3 className="font-playfair text-2xl font-bold text-gold mb-1">Raw Honey</h3>
          <p className="text-gold/60 text-xs font-semibold italic mb-3">The smooth finish that softens the fire.</p>
          <p className="text-gray-600 text-sm leading-relaxed mb-5">
            Unlike processed honey, raw honey retains all its natural enzymes, pollen, and antioxidants — making it a true superfood with antibacterial properties and lasting energy.
          </p>
          <BenefitList items={honeyBenefits} />
        </div>
      </div>
    </section>
  );
}

function IngredientCard({ name, subtitle, image, bgClass, benefitItems, testId }) {
  return (
    <div className={`${bgClass} rounded-2xl overflow-hidden`} data-testid={testId}>
      <div className="h-48">
        <img src={image} alt={name} className="w-full h-full object-cover" />
      </div>
      <div className="p-8">
        <h3 className="font-playfair text-2xl font-bold text-gold mb-1">{name}</h3>
        <p className="text-gold/60 text-xs font-semibold italic mb-3">{subtitle}</p>
        <p className="text-gray-600 text-sm leading-relaxed mb-5">
          {name === "Organic Lemon" && "Fresh-squeezed lemon delivers a powerful hit of Vitamin C and flavonoids that protect your cells and support your immune system — while helping your body alkalise despite its natural acidity."}
          {name === "Organic Apple Cider Vinegar" && "Unfiltered ACV contains the 'mother' — a colony of beneficial bacteria and enzymes that make it one of the most powerful natural health tonics for gut health and blood sugar balance."}
          {name === "Organic Green Tea" && "Green tea is loaded with polyphenols that reduce inflammation and protect against cellular damage. Its unique L-theanine + caffeine combination provides calm, focused energy without the jitters."}
        </p>
        <BenefitList items={benefitItems} />
      </div>
    </div>
  );
}

function IngredientsRow() {
  return (
    <section className="pb-16 px-6" data-testid="ingredients-row">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
        <IngredientCard name="Organic Lemon" subtitle="Bright, zesty, and loaded with vitamin C." image="https://images.unsplash.com/photo-1624436518795-a2c1c77d6896?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" bgClass="lemon-bg" benefitItems={lemonBenefits} testId="lemon-card" />
        <IngredientCard name="Organic Apple Cider Vinegar" subtitle="The gut-reset your body craves." image="https://customer-assets.emergentagent.com/job_wellness-shots/artifacts/vy0md0kq_Home-Made-Apple-Cider-Vinegar-scaled.jpg" bgClass="acv-bg" benefitItems={acvBenefits} testId="acv-card" />
        <IngredientCard name="Organic Green Tea" subtitle="Calm, focused energy — no crash." image="https://images.unsplash.com/photo-1668733521099-c120aa4ebbdc?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" bgClass="greentea-bg" benefitItems={greenTeaBenefits} testId="greentea-card" />
      </div>
    </section>
  );
}

function BioactivesTable() {
  return (
    <div className="space-y-3" data-testid="bioactives-table">
      <div className="hidden md:grid md:grid-cols-12 gap-4 px-5 text-xs font-bold tracking-widest text-gold uppercase">
        <span className="md:col-span-3">Ingredient</span>
        <span className="md:col-span-4">Primary Bioactives</span>
        <span className="md:col-span-5">Core Health Benefits</span>
      </div>
      {bioactives.map((row) => (
        <div key={row.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 bg-white border border-gold/10 rounded-xl px-5 py-4" data-testid={`bioactive-${row.id}`}>
          <p className="md:col-span-3 font-playfair font-bold text-brown-dark">{row.ingredient}</p>
          <p className="md:col-span-4 text-sm text-gold font-semibold">{row.compounds}</p>
          <p className="md:col-span-5 text-sm text-gray-500">{row.benefit}</p>
        </div>
      ))}
    </div>
  );
}

function SynergyEffectCard({ effect }) {
  return (
    <div className="bg-white rounded-2xl border border-gold/10 p-7" data-testid={`synergy-${effect.id}`}>
      <div className="flex items-center gap-3 mb-1">
        <span className="w-10 h-10 rounded-full bg-honey-tint text-gold flex items-center justify-center flex-shrink-0">{effect.icon}</span>
        <div>
          <h3 className="font-playfair text-lg font-bold text-brown-dark leading-snug">{effect.title}</h3>
          <p className="text-gold text-xs font-semibold italic">{effect.subtitle}</p>
        </div>
      </div>
      <p className="text-gray-500 text-sm leading-relaxed mt-3">{effect.text}</p>
    </div>
  );
}

function SynergyStackSection() {
  return (
    <section className="py-16 md:py-20 px-6 bg-cream" data-testid="synergy-stack-section">
      <div className="max-w-6xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <p className="text-xs font-bold tracking-widest text-gold uppercase mb-3">Better Together</p>
          <h2 className="font-playfair text-3xl md:text-4xl font-bold text-brown-dark mb-4">The Synergy of the Stack</h2>
          <p className="text-gray-600 text-sm md:text-base leading-relaxed" data-testid="synergy-intro">
            Combining organic ginger, green tea, lemon, raw honey, and apple cider vinegar (ACV) creates a potent botanical stack. Each ingredient offers strong benefits on its own, but when blended, their active compounds work together to amplify bioavailability, digestion, metabolic health, and immune support.
          </p>
        </div>
        <h3 className="font-playfair text-xl md:text-2xl font-bold text-brown-dark mb-5 text-center">Ingredient Profiles & Key Bioactives</h3>
        <BioactivesTable />
        <h3 className="font-playfair text-xl md:text-2xl font-bold text-brown-dark mt-14 mb-2 text-center">Key Synergistic Effects</h3>
        <p className="text-gray-500 text-sm text-center mb-8">The real power of this blend comes from how these five ingredients interact chemically in your body.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {synergyEffects.map((effect) => (
            <SynergyEffectCard key={effect.id} effect={effect} />
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyItWorksGrid() {
  return (
    <section className="py-16 px-6 bg-honey-tint" data-testid="why-it-works-benefits">
      <div className="max-w-3xl mx-auto text-center">
        <p className="text-xs font-bold tracking-widest text-gold uppercase mb-3">Simple Science</p>
        <h2 className="font-playfair text-3xl md:text-4xl font-bold text-brown-dark mb-8">Why It Works</h2>
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
          {whyItWorksItems.map((item) => (
            <div key={item.name} className="bg-white rounded-xl p-4 border border-gold/10" data-testid={`simple-${item.id}`}>
              <Star size={16} className="text-gold mx-auto mb-2" />
              <p className="font-bold text-brown-dark text-sm">{item.name}</p>
              <p className="text-gray-500 text-xs mt-1">{item.action}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SynergyCta() {
  return (
    <section className="cta-gradient py-16 md:py-24 px-6 text-center" data-testid="synergy-cta">
      <div className="max-w-2xl mx-auto">
        <h2 className="font-playfair text-3xl md:text-5xl font-bold text-white italic mb-6">Together, They're Unstoppable</h2>
        <p className="text-white/70 text-base md:text-lg mb-8 leading-relaxed">
          Each ingredient in Ken's Gold is powerful alone. But when combined, they create a synergistic effect that amplifies every benefit. The result? A single shot that supports your immune system, energizes your body, calms your gut, and sharpens your mind — all at once.
        </p>
        <Link to="/shop" className="btn-gold text-lg" data-testid="benefits-cta-btn">Take Your First Shot <ArrowRight size={18} /></Link>
      </div>
    </section>
  );
}

/* ─── Main Component ─── */

export default function Benefits() {
  useSEO({
    title: "Ingredients & Benefits",
    description: "Discover the 5 powerful ingredients in Ken's Gold: Organic Ginger, Organic Lemon, Raw Honey, Organic Apple Cider Vinegar & Organic Green Tea. Learn how each supports immunity, digestion, energy, and wellness.",
  });

  return (
    <div data-testid="benefits-page">
      <BenefitsHero />
      <FeelSection />
      <GingerAndHoneySection />
      <IngredientsRow />
      <SynergyStackSection />
      <WhyItWorksGrid />
      <SynergyCta />
    </div>
  );
}
